// Phase 8.1 — Customer-only Account hub
// Replaces the prior generic Account/Settings dashboard for customer-only
// users. Multi-role users still see their full Dashboard (they never reach
// here because Dashboard's redirect only fires for customer-only).
//
// Surface: Previous Orders · Order Tracking · Account Settings ·
//          Password Change · Logout. Nothing else.

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Settings as SettingsIcon, KeyRound, LogOut, Receipt } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/customer/orders");
        setOrders(data?.orders || []);
      } catch (e) {
        // Customer orders endpoint may not exist yet; degrade silently
        if (e?.response?.status !== 404) setErr(apiErrorMessage(e));
        setOrders([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (!user) return null;

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
    nav("/");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8" data-testid="customer-account">
      <header>
        <div className="eyebrow">Your account</div>
        <h1 className="font-anton text-3xl sm:text-4xl uppercase mt-1 text-white">
          Hi {(user.name || user.email || "").split(" ")[0]}
        </h1>
        <p className="text-lifted-dim mt-2 text-sm">
          Track your orders, update your settings, and manage your password.
        </p>
      </header>

      {/* Quick links */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="customer-quicklinks">
        <QuickLink to="#orders" icon={Package} label="Previous Orders" testid="ql-orders" />
        <QuickLink to="/account/monthly-box" icon={Package} label="My Monthly Box" testid="ql-monthly-box" />
        <QuickLink to="/settings" icon={SettingsIcon} label="Account Settings" testid="ql-settings" />
        <QuickLink to="/auth/forgot" icon={KeyRound} label="Change Password" testid="ql-password" />
        <button
          onClick={handleLogout}
          className="text-left flex items-center gap-3 p-4 rounded-xl border border-white/10 hover:border-rose-400/50 hover:bg-rose-400/5 transition-colors"
          data-testid="ql-logout"
        >
          <span className="h-10 w-10 rounded-lg bg-rose-400/15 text-rose-200 flex items-center justify-center">
            <LogOut size={18} />
          </span>
          <span className="font-bebas tracking-[0.16em] uppercase text-sm text-white">Logout</span>
        </button>
      </section>

      {/* Profile snapshot */}
      <section className="glass-panel p-6" data-testid="customer-profile">
        <div className="eyebrow text-lifted-dim">Profile</div>
        <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-lifted-mute">Name</dt>  <dd className="text-lifted-text">{user.name || "—"}</dd>
          <dt className="text-lifted-mute">Email</dt> <dd className="text-lifted-text">{user.email}</dd>
          <dt className="text-lifted-mute">Phone</dt> <dd className="text-lifted-text">{user.phone || "—"}</dd>
          <dt className="text-lifted-mute">Status</dt>
          <dd className="text-lifted-green uppercase font-bebas tracking-[0.2em]">{user.status}</dd>
        </dl>
      </section>

      {/* Previous orders */}
      <section id="orders" className="glass-panel p-6" data-testid="customer-orders">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow text-lifted-dim">Previous orders</div>
            <h2 className="font-anton uppercase text-xl text-white mt-1">Order history</h2>
          </div>
          <Receipt className="h-5 w-5 text-lifted-green" />
        </div>
        {loading ? (
          <p className="text-sm text-lifted-dim">Loading your orders…</p>
        ) : err ? (
          <p className="text-sm text-rose-300">{err}</p>
        ) : orders.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-lifted-dim text-sm">You haven't placed any orders yet.</p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 font-bebas uppercase tracking-[0.18em] bg-lifted-green text-black rounded hover:bg-lifted-green/90 text-xs"
              data-testid="customer-shop-cta"
            >
              Shop Lifted
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-white/5" data-testid="customer-orders-list">
            {orders.map((o) => (
              <li key={o.id} className="py-3 flex items-center justify-between gap-3 text-sm" data-testid={`customer-order-${o.id}`}>
                <Link
                  to={`/account/orders/${o.id}`}
                  className="min-w-0 flex-1 hover:opacity-80 transition-opacity"
                  data-testid={`customer-order-link-${o.id}`}
                >
                  <div className="text-white truncate">{o.order_id || o.id}</div>
                  <div className="text-[11px] text-lifted-dim truncate">
                    {o.created_at?.slice(0, 10)} · {o.dropoff_address || "—"}
                  </div>
                </Link>
                <span className="text-[10px] uppercase tracking-[0.16em] font-bebas text-lifted-green">
                  {o.fulfillment_status || o.status || "—"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center gap-3 p-4 rounded-xl border border-white/10 hover:border-lifted-green/50 hover:bg-lifted-green/5 transition-colors"
    >
      <span className="h-10 w-10 rounded-lg bg-lifted-green/15 text-lifted-green flex items-center justify-center">
        <Icon size={18} />
      </span>
      <span className="font-bebas tracking-[0.16em] uppercase text-sm text-white">{label}</span>
    </Link>
  );
}
